import React, { useState, useEffect } from 'react';
import { Button, message, List, Popconfirm, Space, Divider, Tag, notification, Modal, Input, Dropdown, Tooltip } from 'antd';
import { BsFillTrashFill } from 'react-icons/bs';
import { FaEdit, FaEye, FaFileAlt } from 'react-icons/fa';
import { IoDocumentAttachSharp } from 'react-icons/io5';
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined, MoreOutlined } from '@ant-design/icons';
import * as S from './styles';
import listClientFiles from '../../../../services/files/list';
import uploadClientFile from '../../../../services/files/upload';
import deleteClientFile from '../../../../services/files/delete';
import viewClientFile from '../../../../services/files/view';
import moveClientFile from '../../../../services/files/move';
import { listDocumentFolders, createDocumentFolder, updateDocumentFolder, deleteDocumentFolder } from '../../../../services/documentFolders';
import { listDocumentsByClient, deleteDocument } from '../../../../services/documentGenerated';
import { isAdmin } from 'utils/permissions';
import { getErrorMessage } from 'utils/errorMessage';
import OpenInEditor from '../OpenInEditor';

function DocumentsClient({ clientId, processoId }) {
  const userIsAdmin = isAdmin();
  // "Documentos Gerados" hoje não sabe filtrar por processo — mostrar essa
  // lista dentro de uma aba de um processo específico exibiria documentos
  // de outros processos do mesmo cliente, o que seria enganoso.
  const showGeneratedDocuments = !processoId;

  const [loading, setLoading] = useState(false);
  const [incorrectUpload, setIncorrectUpload] = useState(false);
  const [incorrectPhrase, setIncorrectPhrase] = useState('');
  const [file, setFile] = useState();
  const [fileList, setFileList] = useState([]);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  // Pastas
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = "Todos"
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');

  const loadFolders = async () => {
    if (!clientId) { setFolders([]); return; }
    try {
      const data = await listDocumentFolders({ clientId, processoId });
      setFolders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
    }
  };

  const openNewFolderModal = () => {
    setEditingFolder(null);
    setFolderName('');
    setFolderModalOpen(true);
  };

  const openEditFolderModal = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderModalOpen(true);
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) {
      message.error('Informe o nome da pasta');
      return;
    }
    try {
      if (editingFolder) {
        await updateDocumentFolder(editingFolder.id, folderName.trim());
        message.success('Pasta renomeada');
      } else {
        await createDocumentFolder({ name: folderName.trim(), clientId, processoId });
        message.success('Pasta criada');
      }
      setFolderModalOpen(false);
      await loadFolders();
    } catch (error) {
      message.error(getErrorMessage(error) || 'Erro ao salvar pasta');
    }
  };

  const handleDeleteFolder = async (folder) => {
    try {
      await deleteDocumentFolder(folder.id);
      message.success('Pasta excluída');
      if (selectedFolderId === folder.id) setSelectedFolderId(null);
      await loadFolders();
      await handleList();
    } catch (error) {
      message.error(getErrorMessage(error) || 'Erro ao excluir pasta');
    }
  };

  const handleMoveFile = async (item, targetFolderId) => {
    try {
      await moveClientFile(item.id, targetFolderId);
      message.success('Arquivo movido');
      await handleList();
    } catch (error) {
      message.error(getErrorMessage(error) || 'Erro ao mover arquivo');
    }
  };

  // Dropzone handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setTimeout(() => handleUploadData(), 100);
    }
  };

  // Carregar documentos gerados do localStorage
  const loadGeneratedDocs = async () => {
    if (clientId && showGeneratedDocuments) {
      try {
        const response = await listDocumentsByClient(clientId);
        if (response.success) {
          setGeneratedDocs(response.data);
        }
      } catch (error) {
        console.error('Erro ao carregar documentos gerados:', error);
      }
    }
  };

  useEffect(() => {
    handleList();
    loadGeneratedDocs();
    loadFolders();
    // eslint-disable-next-line
  }, [clientId, processoId]);

  const handleUploadFile = e => {
    setFile(e.target.files[0]);
    setIncorrectUpload(false);
    setIncorrectPhrase('');
    // Após selecionar, já faz upload
    setTimeout(() => handleUploadData(), 100);
  };

  async function handleList() {
    try {
      setLoading(true);

      // Buscar documentos (S3)
      if (clientId) {
        const responseData = await listClientFiles({ clientId, processoId });
        setFileList(responseData?.data?.assets || []);
      } else {
        setFileList([]);
      }

      // Carregar documentos gerados do localStorage
      if (clientId && showGeneratedDocuments) {
        try {
          const response = await listDocumentsByClient(clientId);
          if (response.success) {
            setGeneratedDocs(response.data);
          }
        } catch (error) {
          console.error('Erro ao carregar documentos gerados:', error);
        }
      }
    } catch (error) {
      console.error('Erro ao listar documentos:', error);
      notification.error({
        message: 'Erro',
        description: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }  const handleUploadData = async () => {
    const data = new FormData();
    if (file) {
      setLoading(true);
      setIncorrectUpload(false);
      try {
        data.append('file', file);
        await uploadClientFile(data, { clientId, processoId, folderId: selectedFolderId });
        message.success(`${file.name} carregado com sucesso!`);
        await handleList();
        setFile(null); // Resetar o arquivo após o envio
      } catch (error) {
        console.error('Erro ao carregar arquivo:', error);
        setIncorrectUpload(true);
        setIncorrectPhrase(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    } else {
      setIncorrectUpload(true);
      setIncorrectPhrase('É necessário selecionar um arquivo.');
    }
  };

  const handleOpenFile = async (item) => {
    try {
      const res = await viewClientFile(item.id);
      const url = res?.data?.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        message.error('Não foi possível gerar link do arquivo');
      }
    } catch (error) {
      console.error('Erro ao abrir arquivo:', error);
      message.error(getErrorMessage(error));
    }
  };

  const handleOpenGeneratedDoc = async (item) => {
    const assetId = item?.finalFileAssetId || item?.finalFileAsset?.id || item?.htmlFileAssetId || item?.htmlFileAsset?.id;
    if (assetId) {
      try {
        const res = await viewClientFile(assetId);
        const url = res?.data?.url;
        if (url) {
          window.open(url, '_blank');
          return;
        }
      } catch (error) {
        console.error('Erro ao abrir documento final:', error);
      }
    }
    message.info('Documento ainda não finalizado; abrindo editor.');
  };

    const handleDeleteFile = async (item, index) => {
    try {
      setLoading(true);
      await deleteClientFile(item.id);
      await handleList();
      message.success(`${item.name} excluído com sucesso!`);
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      message.error(`Erro ao excluir ${item.name}. Por favor, tente novamente.`);
    }
  };

  const handleDeleteGeneratedDoc = async (index) => {
    try {
      const docToDelete = generatedDocs[index];
      const response = await deleteDocument(docToDelete.id);
      
      if (response.success) {
        const updatedDocs = generatedDocs.filter((_, i) => i !== index);
        setGeneratedDocs(updatedDocs);
        message.success('Documento excluído com sucesso!');
      } else {
        message.error('Erro ao excluir documento. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao excluir documento gerado:', error);
      message.error('Erro ao excluir documento. Por favor, tente novamente.');
    }
  };

  const filesInView = selectedFolderId === null
    ? fileList
    : fileList.filter(item => item.folderId === selectedFolderId);

  return (
    <S.Container style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ width: 168, flexShrink: 0, borderRight: '1px solid var(--yrm-border)', paddingRight: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--yrm-text-3)', textTransform: 'uppercase' }}>Pastas</span>
          <Tooltip title="Nova pasta">
            <Button type="text" size="small" icon={<PlusOutlined />} onClick={openNewFolderModal} />
          </Tooltip>
        </div>

        <div
          onClick={() => setSelectedFolderId(null)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
            background: selectedFolderId === null ? 'var(--yrm-tint-azul)' : 'transparent',
            fontWeight: selectedFolderId === null ? 600 : 400,
            marginBottom: 2,
          }}
        >
          <span><FolderOutlined style={{ marginRight: 6 }} />Todos</span>
          <span style={{ color: 'var(--yrm-text-3)' }}>{fileList.length}</span>
        </div>

        {folders.map(folder => (
          <div
            key={folder.id}
            onClick={() => setSelectedFolderId(folder.id)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
              background: selectedFolderId === folder.id ? 'var(--yrm-tint-azul)' : 'transparent',
              fontWeight: selectedFolderId === folder.id ? 600 : 400,
              marginBottom: 2,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <FolderOutlined style={{ marginRight: 6 }} />{folder.name}
            </span>
            <Space size={2}>
              <span style={{ color: 'var(--yrm-text-3)', fontSize: 12 }}>
                {fileList.filter(f => f.folderId === folder.id).length}
              </span>
              <Button
                type="text" size="small" icon={<EditOutlined />}
                onClick={(e) => { e.stopPropagation(); openEditFolderModal(folder); }}
              />
              <Popconfirm
                title="Excluir esta pasta?"
                description="Os arquivos voltam para 'Todos'."
                onConfirm={(e) => { e?.stopPropagation?.(); handleDeleteFolder(folder); }}
                onCancel={(e) => e?.stopPropagation?.()}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
              </Popconfirm>
            </Space>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
      <input
        type="file"
        id="upload-doc-client"
        style={{ display: 'none' }}
        onChange={handleUploadFile}
        disabled={loading}
      />

      <S.Dropzone
        dragActive={dragActive}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('upload-doc-client').click()}
        role="button"
        tabIndex={0}
      >
        <IoDocumentAttachSharp size={18} style={{ marginRight: 8 }} />
        <span>{loading ? 'Enviando...' : 'Arraste ou clique para adicionar documento'}</span>
      </S.Dropzone>

      {incorrectUpload && <S.UploadError>{incorrectPhrase}</S.UploadError>}

      {/* Documentos Gerados */}
      {generatedDocs.length > 0 && (
        <>
          <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
            <Tag color="green">Documentos Gerados</Tag>
          </Divider>
          <List
            style={{ width: '100%', marginBottom: 16 }}
            dataSource={generatedDocs}
            renderItem={(item, index) => (
              <List.Item style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                border: '1px solid #e6f7ff', 
                borderRadius: '8px',
                margin: '8px 0',
                padding: '12px 16px',
                background: 'var(--yrm-tint-verde)'
              }}>
                <S.DocumentInfo>
                  <FaFileAlt style={{ color: '#52c41a', fontSize: '16px' }} />
                  <span style={{ fontWeight: 500, color: 'var(--yrm-text)' }}>{item.name}</span>
                  <Tag size="small" color="green">Gerado</Tag>
                </S.DocumentInfo>
                
                <Space>
                  <OpenInEditor 
                    document={{
                      id: item.id,
                      name: item.name,
                      content: item.content,
                      isGenerated: true
                    }} 
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<FaEye />}
                    onClick={() => handleOpenGeneratedDoc(item)}
                    title={item.finalFileAssetId ? 'Visualizar PDF final' : 'Visualizar documento'}
                    style={{ color: 'var(--yrm-link)' }}
                  />
                  
                  {userIsAdmin && (
                    <Popconfirm
                      title="Tem certeza que deseja excluir este documento?"
                      onConfirm={() => handleDeleteGeneratedDoc(index)}
                      okText="Sim"
                      cancelText="Cancelar"
                    >
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<BsFillTrashFill />}
                        title="Excluir documento"
                        danger
                      />
                    </Popconfirm>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </>
      )}

     
      {filesInView.length > 0 && (
        <>
          <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
            <Tag color="blue">Documentos</Tag>
          </Divider>
        </>
      )}

      <List
        style={{ width: '100%', marginTop: filesInView.length > 0 ? 0 : 16 }}
        dataSource={filesInView}
        locale={{ emptyText: 'Nenhum documento encontrado.' }}
        renderItem={(item, index) => (
          <List.Item style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            border: '1px solid var(--yrm-border)', 
            borderRadius: '8px',
            margin: '8px 0',
            padding: '12px 16px',
            background: 'var(--yrm-surface)'
          }}>
            <S.DocumentInfo>
              <IoDocumentAttachSharp style={{ color: 'var(--yrm-link)', fontSize: '16px' }} />
            
              <span style={{ fontWeight: 500, color: 'var(--yrm-text)' }}>{item.nomeOriginal}</span>
            </S.DocumentInfo>
            
            <Space>
              <Button
                type="text"
                size="small"
                icon={<FaEye />}
                onClick={() => handleOpenFile(item)}
                title="Visualizar documento"
                style={{ color: 'var(--yrm-link)' }}
              />
              
              <Button
                type="text"
                size="small"
                icon={<FaEdit />}
                onClick={() => handleOpenFile(item)}
                title="Abrir documento"
                style={{ color: '#52c41a' }}
              />

              <OpenInEditor document={item} />

              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'none', label: 'Sem pasta', disabled: !item.folderId },
                    ...folders.map(folder => ({
                      key: folder.id,
                      label: folder.name,
                      disabled: item.folderId === folder.id,
                    })),
                  ],
                  onClick: ({ key }) => handleMoveFile(item, key === 'none' ? null : key),
                }}
              >
                <Button type="text" size="small" icon={<MoreOutlined />} title="Mover para pasta" />
              </Dropdown>

              {userIsAdmin && (
                <Popconfirm
                  title="Tem certeza que deseja excluir este arquivo?"
                  onConfirm={() => handleDeleteFile(item, index)}
                  okText="Sim"
                  cancelText="Cancelar"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<BsFillTrashFill />}
                    title="Excluir documento"
                    danger
                  />
                </Popconfirm>
              )}
            </Space>
          </List.Item>
        )}
      />
      </div>

      <Modal
        title={editingFolder ? 'Renomear pasta' : 'Nova pasta'}
        open={folderModalOpen}
        onOk={handleSaveFolder}
        onCancel={() => setFolderModalOpen(false)}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Input
          placeholder="Nome da pasta"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onPressEnter={handleSaveFolder}
          autoFocus
        />
      </Modal>
    </S.Container>
  );
}



export default DocumentsClient;
